import type { Middleware } from "openapi-fetch";
import { clearToken, getToken, setToken } from "./auth";

/**
 * ต่ออายุ access token ของ web-admin (อายุ 15 นาที) ด้วย refresh token ใน cookie
 *
 * refresh token **ไม่เคยผ่านมือ JavaScript** — API ตั้งเป็น cookie httpOnly (`sdds_refresh`,
 * Path=/v1/auth) เมื่อส่ง `X-Auth-Transport: cookie` มา สคริปต์ที่หลุดเข้ามาในหน้าจึงขโมย
 * ไปใช้ต่อไม่ได้ ที่อยู่ใน localStorage มีแค่ access token อายุสั้น
 *
 * ต่ออายุสองจังหวะ: ก่อนยิง (token เหลือไม่ถึง 60 วิ) และหลังโดน 401 (เผื่อนาฬิกาเครื่อง
 * เพี้ยน) — ต่อไม่ได้แปลว่า session จบจริง ต้องพาไปหน้า login ไม่ใช่ค้างอยู่หน้าที่ทุก
 * request พัง
 */

export const AUTH_TRANSPORT_HEADER = { "X-Auth-Transport": "cookie" } as const;

const REFRESH_LEEWAY_MS = 60_000;
const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "";

/** เวลาหมดอายุ (ms) ของ JWT ปัจจุบัน — `dev-admin` ของโหมด memory ไม่ใช่ JWT และไม่หมดอายุ */
function expiresAt(token: string): number | null {
  const [, payload] = token.split(".");
  if (!payload) return null;
  try {
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: unknown };
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isRefreshable(token: string | null): token is string {
  return token !== null && token !== "dev-admin";
}

let inflight: Promise<string | null> | null = null;

/**
 * ขอ access token ใหม่ — single-flight: หลายหน้าจอโหลดพร้อมกันแล้วเจอ token หมดพร้อมกัน
 * ต้องได้การหมุนครั้งเดียว ไม่งั้นใบที่สองจะเอา refresh token ที่เพิ่งถูกหมุนทิ้งไปใช้
 */
export function refreshAccessToken(): Promise<string | null> {
  inflight ??= (async () => {
    try {
      // fetch ตรง ไม่ผ่าน `api` — ไม่งั้น middleware ข้างล่างจะวนเรียกตัวเอง
      const res = await fetch(`${baseUrl}/v1/auth/refresh`, {
        method: "POST",
        headers: AUTH_TRANSPORT_HEADER,
        credentials: "include",
      });
      if (!res.ok) return null;
      const body = (await res.json()) as { access_token?: string };
      if (!body.access_token) return null;
      setToken(body.access_token);
      return body.access_token;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/** session จบแล้ว (refresh ไม่ผ่าน) — ล้าง token แล้วพาไปหน้า login พร้อมบอกเหตุผล */
function endSession(): void {
  clearToken();
  if (!window.location.pathname.startsWith("/login")) {
    window.location.assign("/login?expired=1");
  }
}

/** ออกจากระบบ: เพิกถอน refresh token ที่ server (ลบ cookie ด้วย) แล้วค่อยล้างของในเครื่อง */
export async function logout(): Promise<void> {
  try {
    await fetch(`${baseUrl}/v1/auth/logout`, {
      method: "POST",
      headers: AUTH_TRANSPORT_HEADER,
      credentials: "include",
    });
  } catch {
    // ติดต่อ server ไม่ได้ก็ยังต้องออกจากเครื่องนี้ได้ — token เดิมจะหมดอายุเองใน 30 วัน
  }
  clearToken();
}

const isAuthCall = (req: Request) => new URL(req.url, window.location.origin).pathname.startsWith("/v1/auth/");

// สำเนาของ request ไว้ยิงซ้ำหลังต่ออายุ — body ของ Request อ่านได้ครั้งเดียว ต้อง clone ก่อนส่ง
const retryCopies = new WeakMap<Request, Request>();

export const sessionMiddleware: Middleware = {
  async onRequest({ request }) {
    if (isAuthCall(request) || !request.headers.has("Authorization")) return request;
    let token = getToken();
    if (isRefreshable(token)) {
      const exp = expiresAt(token);
      if (exp !== null && exp - Date.now() < REFRESH_LEEWAY_MS) {
        token = await refreshAccessToken();
        if (!token) {
          endSession();
          return request;
        }
      }
      // header ถูกประกอบก่อนถึง middleware (authHeaders()) อาจเป็นของใบเก่า — ใส่ใบปัจจุบันเสมอ
      request.headers.set("Authorization", `Bearer ${token}`);
    }
    retryCopies.set(request, request.clone());
    return request;
  },
  async onResponse({ request, response }) {
    if (response.status !== 401 || isAuthCall(request) || !request.headers.has("Authorization")) return response;
    if (!isRefreshable(getToken())) return response;
    const token = await refreshAccessToken();
    const copy = retryCopies.get(request);
    if (!token || !copy) {
      endSession();
      return response;
    }
    copy.headers.set("Authorization", `Bearer ${token}`);
    return fetch(copy);
  },
};
